import { EventActions, GiftCardActions, SerialTicketActions } from '../actions'
import { assert } from '../assert'
import type { ButtonProps } from '../components/button/button'
import { handleError } from '../handle-error'
import { ResponseFilm, type ResponseProps } from '../modules/response/response'
import type { ResponseBlockProps } from '../modules/response/response-block'
import type { EventState, EventView, GiftCardView, SerialTicketView } from '../types'

export const getSessionExpiredResponse = ({
    lang,
    scheduleUrl,
    theme,
}: EventState): ResponseProps => ({
    title: lang.session_expired,
    class: 'h-text-center',
    theme,
    blocks: [
        {
            button: {
                text: lang.schedule,
                element: 'a',
                url: scheduleUrl,
            },
        },
    ],
})

export const getSaleExpiredResponse = ({
    lang,
    scheduleUrl,
    theme,
}: EventState): ResponseProps => ({
    title: lang.sale_expired,
    class: 'h-text-center',
    theme,
    blocks: [
        {
            button: {
                text: lang.schedule,
                element: 'a',
                url: scheduleUrl,
            },
        },
    ],
})

const getFilmProps = (state: EventState): ResponseFilm => {
    const {
        details: { datetime },
        production,
        completionData,
    } = state

    const time = datetime.time.toLowerCase()
    const props: ResponseFilm = {
        title: production.name,
        datetime: `${datetime.date} ${time}`,
    }

    if (completionData.ticketLines.length) {
        props.seats = {
            title: completionData.ticketsTitle + ':',
            tickets: completionData.ticketLines,
        }
    }

    return props
}

export const getEventCompletedResponse = (view: EventView): ResponseProps => {
    const { state } = view
    const { lang, production, completionData, theme } = state

    const blocks: ResponseBlockProps[] = []

    if (completionData.ticketLines.length) {
        blocks.push(
            {
                text: [completionData.ticketsSentText, completionData.ticketsDownloadText],
            },
            {
                button: {
                    text: completionData.ticketsButton.text,
                    element: 'a',
                    url: completionData.ticketsButton.url,
                },
            },
        )
    } else {
        // The customer has added products to an existing ticket.
        // TODO: a more reliable way of detecting this scenario?

        blocks.push(
            {
                text: [completionData.invoiceSentText, completionData.invoiceDownloadText],
            },
            {
                button: {
                    text: completionData.invoiceButton.text,
                    element: 'a',
                    url: completionData.invoiceButton.url,
                },
            },
        )
    }

    blocks.push(
        {
            text: [completionData.contactInfo],
        },
        {
            button: {
                text: lang.completed_startNew,
                onClick: async () => {
                    try {
                        await EventActions.reset(view)
                    } catch (error) {
                        handleError(view, error as Error)
                    }
                },
            },
        },
    )

    return {
        title: lang.completed_title,
        poster: production.image,
        theme,
        film: getFilmProps(state),
        blocks,
    }
}

export const getGiftCardCompletedResponse = (view: GiftCardView): ResponseProps => {
    const { state } = view
    const { lang, data } = state
    assert(data)
    const { completionData } = data
    assert(completionData)
    const { cardCode } = completionData

    return {
        theme: state.theme,
        isLoading: state.isLoading,
        title: lang.giftCard_completed_title,
        blocks: [
            {
                text: [lang.giftCard_code],
                center: true,
            },
            {
                text: [cardCode],
                center: true,
                bold: true,
            },
            {
                text: [lang.giftCard_usage, completionData.emailSentText],
            },
            {
                button: {
                    text: completionData.downloadButton.text,
                    element: 'a',
                    url: completionData.downloadButton.url,
                },
            },
            {
                text: [completionData.contactInfo],
            },
            {
                button: getResetButton(view, async () => GiftCardActions.reset(view)),
            },
        ],
    }
}

export const getSerialTicketCompletedResponse = (view: SerialTicketView): ResponseProps => {
    const { state } = view
    const { lang, data } = state
    assert(data)
    const { completionData } = data
    assert(completionData)
    const { ticketCode } = completionData

    return {
        theme: state.theme,
        isLoading: state.isLoading,
        title: lang.serialTicket_completed_title,
        blocks: [
            {
                text: [lang.serialTicket_code],
                center: true,
            },
            {
                text: [ticketCode],
                center: true,
                bold: true,
            },
            {
                text: [lang.serialTicket_usage, completionData.emailSentText],
            },
            {
                button: {
                    text: completionData.downloadButton.text,
                    element: 'a',
                    url: completionData.downloadButton.url,
                },
            },
            {
                text: [completionData.contactInfo],
            },
            {
                button: getResetButton(view, async () => SerialTicketActions.reset(view)),
            },
        ],
    }
}

export const getSoldOutResponse = (state: EventState): ResponseProps => {
    const { lang, production, scheduleUrl } = state

    return {
        theme: state.theme,
        title: lang.soldOut_title,
        poster: production.image,
        film: getFilmProps(state),
        blocks: [
            {
                text: [lang.soldOut_text],
            },
            {
                button: {
                    text: lang.schedule,
                    element: 'a',
                    url: scheduleUrl,
                },
            },
        ],
    }
}

export const getTemporarilyUnavailableResponse = (state: EventState): ResponseProps => {
    const { lang, production, scheduleUrl } = state

    return {
        theme: state.theme,
        title: lang.temporarilyUnavailable_title,
        poster: production.image,
        film: getFilmProps(state),
        blocks: [
            {
                button: {
                    text: lang.schedule,
                    element: 'a',
                    url: scheduleUrl,
                },
            },
        ],
    }
}

export const getPaymentPendingResponse = (
    view: EventView | GiftCardView | SerialTicketView,
    reset: () => Promise<void>,
): ResponseProps => {
    const { state } = view
    const { lang } = state

    return {
        theme: state.theme,
        title: lang.pending_title,
        blocks: [{ text: [lang.pending_text] }, { button: getResetButton(view, reset) }],
    }
}

const getResetButton = (
    view: EventView | GiftCardView | SerialTicketView,
    reset: () => Promise<void>,
): ButtonProps => {
    const { lang } = view.state

    return {
        text: lang.completed_startNew,
        onClick: async () => {
            try {
                await reset()
            } catch (error) {
                handleError(view, error as Error)
            }
        },
    }
}
